/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DashboardComponentsIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  dashboardAction,
  HandleApiActions,
  userManagementApis,
} from "../../../../../redux/actions";
import { Box } from "@mui/material";
import DashboardEEComponents from "./DashboardEEComponents";
import DashboardSwmComponent from "./DashboardSwmComponent";
import DashboardVehicleComponent from "./DashboardVehicleComponent";
import DashboardVehicleSystem from "./DashboardVehicleSystem";

function DashboardComponentsIndex({ type }) {
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const selectedProjectId = selectedProject?.project_id;
  const selectedVariantId = selectedVariant?.variant_id;

  const dispatch = useDispatch();


  const [recentActivitiesDetails, setRecentActivitiesDetails] = useState([]);

  const [userDetailsMap, setUserDetailsMap] = useState(new Map());

  const getRecentActivitiesDetails = async () => {
    let apiDetails = dashboardAction.get_dash_board_activities;
    delete apiDetails.action_type;

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: selectedProjectId,
            variant_id: selectedVariantId,
          },
          show_toast: false,
        }),
      ),
    );
    if (resp?.error) {
      setRecentActivitiesDetails({});
    } else {
      setRecentActivitiesDetails(resp?.data);
    }
  };

  const getUserFullNameById = (userId) => {
    return userDetailsMap.get(userId) || userId;
  };


  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const apiDetails = userManagementApis?.get_users_list_pa;
        delete apiDetails.action_type;
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...apiDetails,
              show_toast: false,
            }),
          ),
        );

        if (!resp?.error) {
          const users = resp?.data;
          const userMap = new Map(
            users.map((user) => [user?.user_id, user?.fullname]),
          );
          setUserDetailsMap(userMap);
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };

    fetchUserDetails();
  }, [dispatch]);

  useEffect(() => {

    if (selectedProject?.project_id && selectedVariant?.variant_id) {
      getRecentActivitiesDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedVariant]);

  const getComponentBasedOnType = () => {
    switch (type) {
      case "EE":
        return (
          <DashboardEEComponents
            recentActivitiesDetails={recentActivitiesDetails}
            getUserFullNameById={getUserFullNameById}
          />
        );
      case "SWM":
        return (
          <DashboardSwmComponent
            recentActivitiesDetails={recentActivitiesDetails}
            getUserFullNameById={getUserFullNameById}
          />
        );
      case "VS":
        return (
          <DashboardVehicleSystem
            recentActivitiesDetails={recentActivitiesDetails}
            getUserFullNameById={getUserFullNameById}
          />
        );
      case "VC":
        return (
          <DashboardVehicleComponent
            recentActivitiesDetails={recentActivitiesDetails}
            getUserFullNameById={getUserFullNameById}
          />
        );
      default:
        return null;
    }
  };

  return <Box>{getComponentBasedOnType()}</Box>;
}

DashboardComponentsIndex.propTypes = {
  type: PropTypes.string,
};

export default DashboardComponentsIndex;
